import React, { useState, useMemo, useEffect } from 'react';
import { X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { colors } from '../constants/colors';
import { UserAvatar } from './UserAvatar';

import { getAuthApiUrl } from '../config/env';

/**
 * Build a backend OAuth start URL for a provider. The backend 302-redirects to
 * the provider's hosted auth page and eventually back to the current path with
 * tokens in the URL fragment.
 */
function oauthStartUrl(provider: 'google' | 'facebook' | 'apple', nextPath: string): string {
  const next = encodeURIComponent(nextPath || '/');
  return `${getAuthApiUrl()}/api/v1/auth/oauth/${provider}/start?next=${next}`;
}

interface AuthPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthenticated: () => void;
  title?: string;
  subtitle?: string;
  initialTab?: 'signin' | 'signup';
  startAtCredentialForm?: boolean;
  sellerAvatarUrl?: string | null;
  sellerUsername?: string | null;
  transparent?: boolean;
}

function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

type InputMode = 'phone' | 'email' | 'username';

function detectMode(value: string): InputMode {
  if (value.includes('@')) return 'email';
  const digitsOnly = value.replace(/\D/g, '');
  if (digitsOnly.length > 0 && digitsOnly === value.replace(/[\s()\-+]/g, '')) return 'phone';
  return 'username';
}

export function AuthPromptModal({
  isOpen,
  onClose,
  onAuthenticated,
  title: _title,
  subtitle: _subtitle,
  initialTab = 'signin',
  startAtCredentialForm = false,
  sellerAvatarUrl,
  sellerUsername,
  transparent = false,
}: AuthPromptModalProps) {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isWatchingShow = location.pathname.startsWith('/watch/');
  const nextPath = location.pathname + location.search;
  const [tab, setTab] = useState<'signin' | 'signup'>(initialTab);
  const [showCredentialForm, setShowCredentialForm] = useState(startAtCredentialForm);

  useEffect(() => {
    if (isOpen) {
      setTab(initialTab);
      setShowCredentialForm(startAtCredentialForm);
    }
  }, [isOpen, initialTab, startAtCredentialForm]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Sign In state
  const [rawInput, setRawInput] = useState('');
  const [signInPassword, setSignInPassword] = useState('');

  // Sign Up state
  const [phoneDigits, setPhoneDigits] = useState('');
  const [email, setEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');

  const mode = useMemo(() => detectMode(rawInput), [rawInput]);
  const displayValue = mode === 'phone' ? formatPhoneNumber(rawInput) : rawInput;

  if (!isOpen) return null;

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    let identifier: string;
    if (mode === 'phone') {
      const digits = rawInput.replace(/\D/g, '').slice(0, 10);
      identifier = `+1${digits}`;
    } else {
      identifier = rawInput.trim();
    }

    try {
      await login(identifier, signInPassword);
      onAuthenticated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (phoneDigits.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }
    if (signUpPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);
    const fullPhoneNumber = `+1${phoneDigits}`;

    try {
      await register(fullPhoneNumber, signUpPassword, email);
      onAuthenticated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const switchTab = (newTab: 'signin' | 'signup') => {
    setTab(newTab);
    setError(null);
    setShowCredentialForm(false);
  };

  return (
    <div style={{ ...styles.overlay, ...(transparent ? { backgroundColor: 'rgba(0, 0, 0, 0.3)' } : {}) }} onClick={onClose}>
      <div style={{ ...styles.card, ...(transparent ? { backgroundColor: 'rgba(255, 255, 255, 0.92)', backdropFilter: 'blur(16px)' } : {}) }} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} style={styles.closeButton}>
          <X size={20} color="#666" />
        </button>

        {/* Snagg logo — small brand mark */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
          <img src="/img/snagg_assets/snagg_combined_color.svg" alt="Snagg" style={{ height: 22 }} />
        </div>

        {/* Seller hero — large on welcome, smaller on sub-screens */}
        {sellerAvatarUrl && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 8 }}>
            <UserAvatar src={sellerAvatarUrl} alt={sellerUsername ?? 'Seller'} size={showCredentialForm ? 48 : (tab === 'signin' ? 80 : 48)} style={{ marginBottom: 10 }} />
            {sellerUsername && (
              <h2 style={{ fontSize: showCredentialForm || tab === 'signup' ? 16 : 18, fontWeight: 700, color: colors.ink.black, textAlign: 'center', margin: 0 }}>
                Welcome to {sellerUsername}'s live show!
              </h2>
            )}
          </div>
        )}

        {error && <div style={styles.error}>{error}</div>}

        {/* ===== SCREEN 1: Welcome / Sign-in options ===== */}
        {tab === 'signin' && !showCredentialForm && (
          <>
            <p style={{ ...styles.subtitle, marginBottom: 20 }}>
              <strong>Create a free account to bid and chat</strong>
            </p>
            <button onClick={() => setShowCredentialForm(true)} style={styles.credentialButton}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
              </svg>
              <span>Sign in with email, phone, or username</span>
            </button>

            <div style={{ ...styles.socialButtons, marginTop: 10 }}>
              <a href={oauthStartUrl('apple', nextPath)} style={{ ...styles.appleButton, textDecoration: 'none' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
                <span>Continue with Apple</span>
              </a>
              <a href={oauthStartUrl('google', nextPath)} style={{ ...styles.googleButton, textDecoration: 'none' }}>
                <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                <span>Continue with Google</span>
              </a>
              <a href={oauthStartUrl('facebook', nextPath)} style={{ ...styles.facebookButton, textDecoration: 'none' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                <span>Continue with Facebook</span>
              </a>
            </div>

            <div style={{ ...styles.divider, margin: '16px 0' }}>
              <div style={styles.dividerLine} />
              <span style={styles.dividerText}>or</span>
              <div style={styles.dividerLine} />
            </div>

            <p style={{ textAlign: 'center', margin: 0 }}>
              <span onClick={() => switchTab('signup')} style={{ color: colors.brand.blue, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                New here? Create an account
              </span>
            </p>
          </>
        )}

        {/* ===== SCREEN 2: Sign-in credential form ===== */}
        {tab === 'signin' && showCredentialForm && (
          <>
            <button onClick={() => setShowCredentialForm(false)} style={styles.backButton}>← Back</button>
            <form onSubmit={handleSignIn} style={styles.form}>
              <div style={styles.field}>
                <label style={styles.label}>Phone, Email, or Username</label>
                <div style={styles.phoneContainer}>
                  {mode === 'phone' && <div style={styles.countryCode}>+1</div>}
                  <input type={mode === 'phone' ? 'tel' : 'text'} value={displayValue} onChange={(e) => setRawInput(e.target.value)} placeholder={mode === 'phone' ? '(555) 123-4567' : 'Phone, email, or username'} style={{ ...styles.input, ...(mode === 'phone' ? { borderRadius: '0 8px 8px 0', borderLeft: 'none' } : {}) }} required autoFocus />
                </div>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Password</label>
                <input type="password" value={signInPassword} onChange={(e) => setSignInPassword(e.target.value)} placeholder="Enter your password" style={styles.input} required />
              </div>
              <div style={{ textAlign: 'right', marginTop: '-8px' }}>
                <span onClick={() => { onClose(); navigate('/forgot-password'); }} style={{ color: colors.brand.blue, fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>Forgot password?</span>
              </div>
              <button type="submit" style={styles.button} disabled={isLoading}>{isLoading ? 'Signing in...' : 'Sign In'}</button>
            </form>
          </>
        )}

        {/* ===== SCREEN 3: Sign-up options ===== */}
        {tab === 'signup' && !showCredentialForm && (
          <>
            <p style={{ ...styles.subtitle, marginBottom: 20 }}>
              <strong>Create a free account</strong>
            </p>

            <div style={styles.socialButtons}>
              <a href={oauthStartUrl('apple', nextPath)} style={{ ...styles.appleButton, textDecoration: 'none' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
                <span>Continue with Apple</span>
              </a>
              <a href={oauthStartUrl('google', nextPath)} style={{ ...styles.googleButton, textDecoration: 'none' }}>
                <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                <span>Continue with Google</span>
              </a>
              <a href={oauthStartUrl('facebook', nextPath)} style={{ ...styles.facebookButton, textDecoration: 'none' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                <span>Continue with Facebook</span>
              </a>
            </div>

            <div style={{ ...styles.divider, margin: '16px 0' }}>
              <div style={styles.dividerLine} />
              <span style={styles.dividerText}>or</span>
              <div style={styles.dividerLine} />
            </div>

            <button onClick={() => setShowCredentialForm(true)} style={styles.credentialButton}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
              </svg>
              <span>Sign up with email or phone</span>
            </button>

            <p style={{ textAlign: 'center', marginTop: 16, marginBottom: 0 }}>
              <span onClick={() => switchTab('signin')} style={{ color: colors.brand.blue, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Already have an account? Sign in
              </span>
            </p>
          </>
        )}

        {/* ===== SCREEN 4: Sign-up credential form ===== */}
        {tab === 'signup' && showCredentialForm && (
          <>
            <button onClick={() => setShowCredentialForm(false)} style={styles.backButton}>← Back</button>
            <form onSubmit={handleSignUp} style={styles.form}>
              <div style={styles.field}>
                <label style={styles.label}>Phone Number</label>
                <div style={styles.phoneContainer}>
                  <div style={styles.countryCode}>+1</div>
                  <input type="tel" value={formatPhoneNumber(phoneDigits)} onChange={(e) => setPhoneDigits(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="(555) 123-4567" style={styles.phoneInput} required autoFocus />
                </div>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" style={styles.input} required />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Password</label>
                <input type="password" value={signUpPassword} onChange={(e) => setSignUpPassword(e.target.value)} placeholder="Create a password" style={styles.input} required minLength={8} />
              </div>
              <p style={{ fontSize: 12, color: '#6B7280', textAlign: 'center', margin: '12px 0 0' }}>
                By continuing, you agree to our{' '}
                <a href="https://snagg.live/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#3B82F6', textDecoration: 'underline' }}>Terms of Service</a>{' '}and{' '}
                <a href="https://snagg.live/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#3B82F6', textDecoration: 'underline' }}>Privacy Policy</a>.
              </p>
              <p style={{ fontSize: 11, color: '#6B7280', textAlign: 'center', margin: '8px 0 0' }}>
                By providing your phone number, you consent to receive a one-time SMS verification code from Snagg. Msg &amp; data rates may apply. Reply STOP to opt out. See our{' '}
                <a href="https://snagg.live/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#3B82F6', textDecoration: 'underline' }}>Privacy Policy</a>{' '}for details.
              </p>
              <button type="submit" style={styles.button} disabled={isLoading}>{isLoading ? 'Creating account...' : 'Create Account'}</button>
            </form>
          </>
        )}

        {/* Continue watching dismiss — only shown on the show-watching page */}
        {isWatchingShow && (
          <p style={{ textAlign: 'center', marginTop: 16, marginBottom: 0 }}>
            <span onClick={onClose} style={{ color: colors.gray[400], fontSize: 13, cursor: 'pointer' }}>
              Continue watching
            </span>
          </p>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  card: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    padding: '32px',
    width: '100%',
    maxWidth: '420px',
    margin: '0 20px',
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
  },
  title: {
    color: colors.ink.black,
    fontSize: '22px',
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: '6px',
  },
  subtitle: {
    color: colors.gray[600],
    fontSize: '14px',
    textAlign: 'center',
    marginBottom: '24px',
  },
  socialButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '10px',
  },
  googleButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid #d0d0d0',
    backgroundColor: colors.white,
    color: '#333',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  facebookButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '12px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#1877F2',
    color: '#fff',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  appleButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '12px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#000',
    color: '#fff',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: colors.gray[300],
  },
  dividerText: {
    color: colors.gray[400],
    fontSize: '13px',
    fontWeight: '500',
  },
  tabs: {
    display: 'flex',
    gap: '0',
    marginBottom: '20px',
    borderBottom: `1px solid ${colors.gray[300]}`,
  },
  tab: {
    flex: 1,
    padding: '10px',
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    fontSize: '14px',
    fontWeight: '600',
    color: colors.gray[600],
    cursor: 'pointer',
  },
  tabActive: {
    color: colors.brand.blue,
    borderBottomColor: colors.brand.blue,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    color: '#333',
    fontSize: '14px',
    fontWeight: '600',
  },
  input: {
    width: '100%',
    boxSizing: 'border-box' as const,
    backgroundColor: colors.white,
    border: '1px solid #d0d0d0',
    borderRadius: '8px',
    padding: '12px 16px',
    fontSize: '16px',
    color: colors.ink.black,
    outline: 'none',
  },
  phoneContainer: {
    display: 'flex',
    alignItems: 'stretch',
  },
  countryCode: {
    backgroundColor: colors.gray[100],
    border: '1px solid #d0d0d0',
    borderRight: 'none',
    borderRadius: '8px 0 0 8px',
    padding: '12px 14px',
    fontSize: '16px',
    color: '#666',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
  },
  phoneInput: {
    flex: 1,
    backgroundColor: colors.white,
    border: '1px solid #d0d0d0',
    borderRadius: '0 8px 8px 0',
    padding: '12px 16px',
    fontSize: '16px',
    color: colors.ink.black,
    outline: 'none',
  },
  credentialButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '12px',
    borderRadius: '8px',
    border: `1px solid ${colors.gray[300]}`,
    backgroundColor: colors.white,
    color: colors.ink.black,
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
    boxSizing: 'border-box' as const,
  },
  backButton: {
    background: 'none',
    border: 'none',
    color: colors.brand.blue,
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    padding: '0 0 16px 0',
    textAlign: 'left' as const,
  },
  button: {
    backgroundColor: colors.brand.blue,
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '14px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '4px',
  },
  error: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '8px',
    padding: '10px',
    color: '#dc2626',
    fontSize: '14px',
    textAlign: 'center',
    marginBottom: '4px',
  },
};
